import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, History, Loader2, ShieldCheck, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, ApiError, canManage, type OrderImportState } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { useTenant } from "./AppShell";
import { Button, cx, Tip } from "./ui";

import { t, msg } from "@/lib/i18n";
const DISMISS_KEY = "lc.orderImport.dismissed";
const PERIODS = [
  { value: "30", label: msg("30 Tage") },
  { value: "90", label: msg("90 Tage") },
  { value: "180", label: msg("6 Monate") },
  { value: "365", label: msg("12 Monate") },
  { value: "all", label: msg("Alle") },
] as const;
type Period = (typeof PERIODS)[number]["value"];

function useOrderImport() {
  const { tenantId } = useTenant();
  const q = useQuery({
    queryKey: ["backend", tenantId],
    queryFn: () => api.backend.get(tenantId),
    refetchInterval: (query) => (query.state.data?.connection?.orderImport?.status === "running" || query.state.data?.connection?.productImport?.status === "running" ? 2500 : false),
  });
  const conn = q.data?.connection;
  return { state: conn?.orderImport ?? null, connected: conn?.status === "active" && conn.authorized !== false, warehouseName: conn?.warehouseName ?? null };
}

/**
 * "Historie aus Lager laden": bisherige Aufträge aus JTL-FFN übernehmen (Migration von FFN Connect).
 * Übernommene Aufträge sind reine Ansicht – sie lösen nie eine Aktion im Shop oder Lager aus.
 */
export function OrderHistoryImportButton() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const { state, connected, warehouseName } = useOrderImport();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<Period>("90");
  const [withShipments, setWithShipments] = useState(true);
  const running = state?.status === "running";
  const start = useMutation({
    mutationFn: () => api.backend.importOrders(tenantId, { period, withShipments }),
    onSuccess: () => {
      setOpen(false);
      try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* ohne Storage egal */ }
      void qc.invalidateQueries({ queryKey: ["backend", tenantId] });
      toast.success("Übernahme gestartet", { description: msg("Der Fortschritt erscheint oben auf der Seite.") });
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Übernahme konnte nicht gestartet werden") : "Übernahme konnte nicht gestartet werden"),
  });

  if (!canManage(role)) return null;
  const button = (
    <Button icon={running ? <Loader2 className="size-4 animate-spin" /> : <History className="size-4" />} disabled={!connected || running} onClick={() => setOpen(true)}>
      {running ? t("Übernahme läuft …") : t("Historie aus Lager laden")}
    </Button>
  );

  return (
    <>
      {connected ? button : <Tip content={t("Zuerst unter Einstellungen → Lager das FFN-Konto verbinden")}><span>{button}</span></Tip>}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
          <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
            <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><History className="size-4 text-fg-subtle" /> {t("Bisherige Aufträge aus dem Lager laden")}</Dialog.Title>
            <Dialog.Description className="mt-1 text-fg-muted text-[13px]">
              {t("Übernimmt die Auftragshistorie aus dem JTL-FFN-Konto")}{warehouseName ? t(" (Lager {warehouseName})", { warehouseName }) : ""} {t("– für Suche, Support und Auswertungen ab dem ersten Tag nach dem Umstieg von FFN Connect.")}
            </Dialog.Description>

            <div className="mt-4">
              <div className="text-[12px] font-medium text-fg-muted mb-1.5">{t("Zeitraum")}</div>
              <div className="inline-flex flex-wrap p-0.5 rounded-lg bg-surface-2 border border-border">
                {PERIODS.map((p) => (
                  <button key={p.value} type="button" onClick={() => setPeriod(p.value)} aria-pressed={period === p.value} className={cx("h-7 px-3 rounded-md text-[13px]", period === p.value ? "bg-surface shadow-sm font-medium" : "text-fg-muted hover:text-fg")}>
                    {t(p.label)}
                  </button>
                ))}
              </div>
            </div>
            <label className="mt-3 flex items-start gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={withShipments} onChange={(e) => setWithShipments(e.target.checked)} className="mt-0.5 accent-ink" />
              <span><span className="font-medium">{t("Sendungen und Trackingnummern mitladen")}</span><span className="block text-[12px] text-fg-muted">{t("dauert bei vielen versendeten Aufträgen etwas länger")}</span></span>
            </label>

            <div className="mt-4 rounded-xl border border-success/25 bg-success-soft/50 p-3.5">
              <p className="font-medium text-[13px] flex items-center gap-1.5"><ShieldCheck className="size-4 text-success" /> {t("Reine Historie – löst nichts aus")}</p>
              <ul className="mt-2 space-y-1 text-[12.5px] text-fg-muted">
                {[
                  "Keine Meldung an Shopify – weder Annahme, Versand noch Tracking, auch nicht bei späteren Sendungen",
                  "Keine Übertragung ans Lager, keine Auftragsregeln, keine Beilagen",
                  "Nicht bearbeit- oder stornierbar – das bleibt beim Ursprungssystem",
                  "Vom Portal selbst übertragene Aufträge werden erkannt und nicht doppelt angelegt",
                ].map((t) => <li key={t} className="flex gap-1.5"><CheckCircle2 className="size-3.5 text-success shrink-0 mt-0.5" /> {t}</li>)}
              </ul>
            </div>
            <p className="mt-3 text-[12px] text-fg-subtle">{t("Die Aufträge erscheinen im Kanal „FFN-Historie“. Ein zweiter Lauf ist gefahrlos und zieht nur den Lagerstatus nach.")}</p>
            {state?.status === "done" && state.finishedAt && <p className="mt-2 text-[12.5px] text-fg-muted">{t("Zuletzt geladen")} {fmtRelative(state.finishedAt)}: {fmtNumber(state.created)} {t("übernommen,")} {fmtNumber(state.updated)} {t("aktualisiert.")}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
              <Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>{t("Historie laden")}</Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

/** Fortschritt der Übernahme; aktualisiert die Auftragsliste währenddessen */
export function OrderHistoryImportBanner() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const { state } = useOrderImport();
  const [dismissed, setDismissed] = useState<string | null>(() => { try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; } });
  const lastStatus = useRef<string | null>(null);

  useEffect(() => {
    if (!state) return;
    if (state.status === "running" || lastStatus.current === "running") void qc.invalidateQueries({ queryKey: ["orders", tenantId] });
    lastStatus.current = state.status;
  }, [state, qc, tenantId]);

  if (!state || dismissed === state.runId) return null;
  const age = state.finishedAt ? Date.now() - new Date(state.finishedAt).getTime() : 0;
  if (state.status !== "running" && age > 60 * 60_000) return null;
  const dismiss = () => { setDismissed(state.runId); try { sessionStorage.setItem(DISMISS_KEY, state.runId); } catch { /* egal */ } };
  const processed = state.created + state.updated + state.skipped;

  return (
    <div className={cx("rounded-xl border px-4 py-3 flex items-start gap-3 text-[13px]", state.status === "failed" ? "border-danger/25 bg-danger-soft" : state.status === "done" ? "border-success/25 bg-success-soft/60" : "border-border bg-surface")}>
      {state.status === "running" ? <Loader2 className="size-4 mt-0.5 animate-spin text-fg-muted" /> : state.status === "done" ? <CheckCircle2 className="size-4 mt-0.5 text-success" /> : <CircleAlert className="size-4 mt-0.5 text-danger" />}
      <div className="min-w-0 flex-1">
        <p className="font-medium">{state.status === "running" ? t("Auftragshistorie wird aus dem Lager übernommen …") : state.status === "done" ? t("Auftragshistorie übernommen") : t("Übernahme abgebrochen")}</p>
        <p className="text-fg-muted mt-0.5 tabular-nums">
          {fmtNumber(processed)}{state.total ? t(" von bis zu {v}", { v: fmtNumber(state.total) }) : ""} {t("gelesen ·")} <b className="text-fg">{fmtNumber(state.created)}</b> {t("übernommen ·")} {fmtNumber(state.updated)} {t("aktualisiert ·")} {fmtNumber(state.skipped)} {t("übersprungen")}
          {state.shipments > 0 && <> · {fmtNumber(state.shipments)} {t("Sendungen")}</>}
        </p>
        {state.status === "failed" && state.error && <p className="mt-1 text-danger text-[12.5px]">{state.error} {t("– ein neuer Lauf setzt fort, ohne Doppelungen.")}</p>}
      </div>
      {state.status !== "running" && <button onClick={dismiss} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:text-fg" aria-label={t("Ausblenden")}><X className="size-3.5" /></button>}
    </div>
  );
}
